import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
	ErrorBoundary,
	Flex,
	Icon,
	InlineErrorFallback,
	Label,
	MenuBar,
} from '@zeal/web-ui';

import { CollapseButton } from '@app/AppContainer/components/StyledComponents';
import { getAppRoutes } from '@app/utils/appRoutes';
import { usePageStore } from '@app/store/pageStore';

import MenuLinks from './MenuLinks/MenuLinks';
import AppMenuBanner from './AppMenuBanner/AppMenuBanner';
import BrandedContent from '@modules/Shared/Components/Branded/BrandedContent';
import ZealLogo from '@app/Components/Shared/ZealLogo';

function MenuHeader() {
	return (
		<img
			alt="zeal logo"
			className="w-28"
			src="https://myzeal.app/assets/imgs/logo.png"
		/>
	);
}

function MenuFooter() {
	const { t } = useTranslation('common');

	return (
		<Flex isColumn align="center" justify="center">
			<AppMenuBanner />
			<BrandedContent
				brandedContent={
					<Flex
						align="center"
						justify="center"
						gap="2"
						className="w-full py-1"
						style={{
							background: '#E9EAEB',
						}}
					>
						<ZealLogo fill="black" />
						<Label.Mid200 variant="black" textAlign="center">
							{t('powered_by_zeal')}
						</Label.Mid200>
					</Flex>
				}
			/>
		</Flex>
	);
}

type AppMenuProps = {
	className?: string;
	children?: React.ReactNode;
	isCollapsed: boolean;
	gridArea: string;
};

const { ownAppMenus, appMainMenus, sectionedMenus } = getAppRoutes();

function AppMenu({ isCollapsed, ...props }: AppMenuProps) {
	const { toggleAppMenu } = usePageStore();

	const { i18n } = useTranslation();

	const handleMenuToggle = useCallback(() => {
		toggleAppMenu();
	}, [toggleAppMenu]);

	return (
		<MenuBar
			className={props.className}
			headerContent={<MenuHeader />}
			footerContent={<MenuFooter />}
			isCollapsed={isCollapsed}
			id="home-sidebar"
		>
			<MenuLinks
				ownAppMenus={ownAppMenus}
				appMainMenus={appMainMenus}
				sectionedMenus={sectionedMenus}
			/>
			<CollapseButton onClick={handleMenuToggle} dir={i18n.dir()}>
				<Flex
					justify="center"
					align="center"
					style={{
						width: '100%',
					}}
				>
					<Icon
						iconName={isCollapsed ? 'chevronDoubleRight' : 'chevronDoubleLeft'}
						size="xs"
						variant="light"
						className="m-auto"
					/>
				</Flex>
			</CollapseButton>
		</MenuBar>
	);
}

const AppMenuWithErrorBoundary = (props: AppMenuProps) => {
	return (
		<ErrorBoundary
			fallback={
				<InlineErrorFallback
					severity="severe"
					style={{
						gridArea: 'menu',
						minHeight: '100%',
					}}
				/>
			}
		>
			<AppMenu {...props} />
		</ErrorBoundary>
	);
};

const StyledAppMenu = styled(AppMenuWithErrorBoundary)`
	grid-area: ${({ gridArea }) => gridArea};
`;

export default memo(StyledAppMenu);
